import type { ReadingPosition } from './useBookUrl'
import { SolrParamsKey } from '#shared/solr'
import { clamp } from '#shared/utils/range'
import { keepPreviousData } from '@tanstack/vue-query'
import { pageQueryOptions } from '~/utils/literature/queries'
import { resolvePageIndexByAnchorFallback } from './page-index-fallback'

/** Range of pages currently loaded (windowed loading around current position). */
export interface LoadedRange {
  start: number
  end: number
}

interface UsePageLoaderOptions {
  bookId: MaybeRefOrGetter<string>
  position: Readonly<Ref<ReadingPosition>>
  batchSize?: number
  enabled?: MaybeRefOrGetter<boolean>
}

/** Load pages in batches around current position. Exposes loadMore() for pagination. */
export function usePageLoader(options: UsePageLoaderOptions) {
  const { bookId, position, batchSize = 10, enabled = true } = options
  const queryClient = useQueryClient()

  const loadedRange = ref<LoadedRange | null>(null)
  const isResolvingPage = ref(false)
  const pageIndexCache = new Map<string, number>()
  const unresolvedPageIndex = new Set<string>()

  async function resolvePageIndexByAnchor(pageNumber: string): Promise<number | null> {
    return resolvePageIndexByAnchorFallback({
      pageNumber,
      bookId: toValue(bookId),
      queryClient,
    })
  }

  async function resolvePageIndex(pageNumber: string): Promise<number | null> {
    if (unresolvedPageIndex.has(pageNumber)) {
      return null
    }

    const cached = pageIndexCache.get(pageNumber)
    if (cached !== undefined) {
      return cached
    }

    try {
      const query = pageQueryOptions(
        () => toValue(bookId),
        () => ({
          [SolrParamsKey.PageNumberString]: pageNumber,
          rows: 1,
        }),
      )

      const res = await queryClient.fetchQuery(query)
      const pageCounter = res?.response?.docs?.[0]?.pageCounter

      queryClient.removeQueries({ queryKey: query.queryKey })

      if (typeof pageCounter === 'number' && Number.isFinite(pageCounter) && pageCounter > 0) {
        pageIndexCache.set(pageNumber, pageCounter)
        return pageCounter
      }

      const pageCounterFromAnchor = await resolvePageIndexByAnchor(pageNumber)
      if (typeof pageCounterFromAnchor === 'number' && Number.isFinite(pageCounterFromAnchor) && pageCounterFromAnchor > 0) {
        pageIndexCache.set(pageNumber, pageCounterFromAnchor)
        return pageCounterFromAnchor
      }

      unresolvedPageIndex.add(pageNumber)

      return null
    }
    catch {
      unresolvedPageIndex.add(pageNumber)
      return null
    }
  }

  async function computeRangeForPosition(pos: ReadingPosition): Promise<LoadedRange> {
    if (!pos.pageNumber) {
      return { start: 1, end: batchSize * 2 }
    }

    isResolvingPage.value = true
    try {
      const pageIndex = await resolvePageIndex(pos.pageNumber)

      if (pageIndex === null) {
        console.warn(`Could not resolve pageIndex for pageNumber: ${pos.pageNumber}`)
        return { start: 1, end: batchSize * 2 }
      }

      const halfBatch = Math.floor(batchSize / 2)
      const range = clamp({
        start: pageIndex - halfBatch - batchSize,
        end: pageIndex + halfBatch + batchSize,
      })

      return range ?? { start: 1, end: batchSize * 2 }
    }
    finally {
      isResolvingPage.value = false
    }
  }

  const queryParams = computed(() => {
    const range = loadedRange.value
    if (!range) {
      return { [SolrParamsKey.Pages]: [0, 0] as [number, number] }
    }
    return {
      [SolrParamsKey.Pages]: [range.start, range.end] as [number, number],
      [SolrParamsKey.Sort]: 'pageCounter asc' as const,
    }
  })

  const query = useQuery({
    ...pageQueryOptions(() => toValue(bookId), queryParams),
    placeholderData: keepPreviousData,
    enabled: computed(() => {
      if (!toValue(enabled)) {
        return false
      }
      const p = toValue(queryParams)
      return !!p.pages
    }),
  })

  const lastPageQuery = useQuery({
    ...pageQueryOptions(() => toValue(bookId), () => ({
      [SolrParamsKey.Id]: `${toValue(bookId)}*`,
      [SolrParamsKey.Sort]: 'pageCounter desc',
      rows: 1,
    })),
    placeholderData: keepPreviousData,
    enabled: computed(() => toValue(enabled)),
  })

  const pages = computed(() => query.data.value?.response?.docs ?? [])
  const hasPages = computed(() => pages.value.length > 0)
  const maxPageCounter = computed(() => lastPageQuery.data.value?.response?.docs?.[0]?.pageCounter ?? null)
  const isLoading = computed(() => isResolvingPage.value || (query.isFetching.value && !query.data.value))
  const isFetching = computed(() => query.isFetching.value)

  const canLoadMore = computed(() => ({
    top: loadedRange.value ? loadedRange.value.start > 1 : false,
    bottom: loadedRange.value !== null
      && (maxPageCounter.value === null || loadedRange.value.end < maxPageCounter.value),
  }))

  const isLoadingMore = ref(false)
  const loadMoreDirection = ref<'top' | 'bottom' | null>(null)

  /** Extend the loaded range and preserve scroll position when loading at top. */
  async function loadMore(
    direction: 'top' | 'bottom',
    scrollContainer?: HTMLElement | null,
  ): Promise<void> {
    if (!loadedRange.value || isLoadingMore.value) {
      return
    }

    isLoadingMore.value = true
    loadMoreDirection.value = direction

    let anchorPageCounter: string | null = null
    let anchorOffset = 0

    if (direction === 'top' && scrollContainer) {
      const firstPageElement = scrollContainer.querySelector('[data-page-counter]') as HTMLElement | null
      anchorPageCounter = firstPageElement?.getAttribute('data-page-counter') ?? null
      anchorOffset = firstPageElement?.getBoundingClientRect().top ?? 0
    }

    if (direction === 'top') {
      const newRange = clamp({
        start: loadedRange.value.start - batchSize,
        end: loadedRange.value.end,
      })
      if (newRange) {
        loadedRange.value = newRange
      }
    }
    else {
      loadedRange.value = {
        start: loadedRange.value.start,
        end: loadedRange.value.end + batchSize,
      }
    }

    await nextTick()
    if (query.isFetching.value) {
      await until(() => !query.isFetching.value).toBe(true)
    }

    if (direction === 'top' && scrollContainer && anchorPageCounter) {
      await waitForDomSettled(scrollContainer)

      const anchorElement = scrollContainer.querySelector(
        `[data-page-counter="${anchorPageCounter}"]`,
      ) as HTMLElement | null

      if (anchorElement) {
        const newAnchorOffset = anchorElement.getBoundingClientRect().top
        const scrollDelta = newAnchorOffset - anchorOffset
        if (Math.abs(scrollDelta) > 1) {
          scrollContainer.scrollTop += scrollDelta
        }
      }
    }

    isLoadingMore.value = false
    loadMoreDirection.value = null
  }

  watch(
    position,
    async (pos) => {
      if (!toValue(enabled)) {
        return
      }

      const targetRange = await computeRangeForPosition(pos)

      if (!loadedRange.value) {
        loadedRange.value = targetRange
        return
      }

      if (!pos.pageNumber) {
        return
      }

      const pageIndex = pageIndexCache.get(pos.pageNumber)

      if (pageIndex === undefined) {
        loadedRange.value = targetRange
        return
      }

      // Skip range update if target is already loaded (avoids Safari scroll race)
      const isWithinRange = pageIndex >= loadedRange.value.start
        && pageIndex <= loadedRange.value.end

      if (!isWithinRange) {
        loadedRange.value = targetRange
      }
    },
    { immediate: true },
  )

  return {
    pages,
    hasPages,
    isLoading,
    isFetching,
    loadedRange: readonly(loadedRange),
    canLoadMore,
    isLoadingMore: readonly(isLoadingMore),
    loadMoreDirection: readonly(loadMoreDirection),
    loadMore,
  }
}

function waitForDomSettled(
  container: HTMLElement,
  options: { timeoutMs?: number, stabilizeMs?: number } = {},
): Promise<void> {
  const { timeoutMs = 2000, stabilizeMs = 50 } = options

  return new Promise((resolve) => {
    let stabilizeTimer: ReturnType<typeof setTimeout> | null = null
    let timeoutTimer: ReturnType<typeof setTimeout> | null = null
    let observer: MutationObserver | null = null

    const cleanup = () => {
      if (stabilizeTimer) {
        clearTimeout(stabilizeTimer)
      }
      if (timeoutTimer) {
        clearTimeout(timeoutTimer)
      }
      observer?.disconnect()
    }

    const scheduleResolve = () => {
      if (stabilizeTimer) {
        clearTimeout(stabilizeTimer)
      }
      stabilizeTimer = setTimeout(() => {
        cleanup()
        resolve()
      }, stabilizeMs)
    }

    observer = new MutationObserver(() => {
      scheduleResolve()
    })

    observer.observe(container, {
      childList: true,
      subtree: true,
    })

    timeoutTimer = setTimeout(() => {
      cleanup()
      resolve()
    }, timeoutMs)

    scheduleResolve()
  })
}
